import { useEffect, useMemo, useState } from "react";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useParcelReport } from "../../store/useParcelReport";
import { usePartners } from "../../store/usePartners";
import { useDropLocations } from "../../store/useDropLocations";
import { useAuth } from "../../store/useAuth";
import { storeLabel } from "../../utils/storeLabel";
import { ParcelReportChart } from "../../components/charts/ParcelReport.chart";
import { ParcelReportIndexChart } from "../../components/charts/ParcelReportIndex.chart";
import {
  IParcelReportFilters,
  ParcelReportFilters,
} from "./ParcelReportFilters";
import { ParcelReportKpis } from "./ParcelReportKpis";
import { SegmentedToggle } from "./SegmentedToggle";
import {
  ALL_CARRIERS,
  GroupBy,
  buildBuckets,
  buildSeries,
  defaultRange,
  fileSlug,
  isReportDay,
  reportStores,
} from "./parcelReportData";

type ParcelsView = "type" | "store";

const PARCELS_VIEW_OPTIONS: { value: ParcelsView; label: string }[] = [
  { value: "type", label: "By type" },
  { value: "store", label: "By store" },
];

type IndexView = "period" | "cumulative";

const PER_PERIOD_LABELS: Record<GroupBy, string> = {
  day: "Per day",
  week: "Per week",
  month: "Per month",
};

const BUCKET_HEADINGS: Record<GroupBy, string> = {
  day: "Day",
  week: "Week starting",
  month: "Month",
};

const GROUPING_SLUGS: Record<GroupBy, string> = {
  day: "daily",
  week: "weekly",
  month: "monthly",
};

function ParcelReportScreen() {
  /** Hooks */
  const { parcelReport, getParcelReport, clearParcelReport, isLoading, error } =
    useParcelReport();
  const { couriers, getCouriers } = usePartners();
  const { dropLocations, getDropLocations } = useDropLocations();
  const currentUser = useAuth((state) => state.user);
  // Only a store admin is pinned to one store, by the backend; everyone else
  // who can open the page picks any store.
  const isStoreAdmin = Number(currentUser?.user_role ?? 0) === 5;

  /** State */
  const [filters, setFilters] = useState<IParcelReportFilters>(() => ({
    carrierPartnerId: "",
    locationId: "",
    ...defaultRange(),
  }));
  const [groupBy, setGroupBy] = useState<GroupBy>("day");
  const [parcelsView, setParcelsView] = useState<ParcelsView>("type");
  const [indexView, setIndexView] = useState<IndexView>("cumulative");
  // `couriers` starts as [], so "none configured" is only known once loaded.
  const [couriersLoaded, setCouriersLoaded] = useState(false);
  const { carrierPartnerId, locationId, dateFrom, dateTo } = filters;

  /** Effects */
  useEffect(() => {
    Promise.resolve(getCouriers()).finally(() => setCouriersLoaded(true));
    getDropLocations();
    return () => clearParcelReport();
  }, [getCouriers, getDropLocations, clearParcelReport]);

  // All carriers is preselected. The first carrier in the list, picked
  // before, is whichever name sorts first, not the one that matters.
  useEffect(() => {
    if (!carrierPartnerId && couriers?.length) {
      setFilters((prev) => ({ ...prev, carrierPartnerId: ALL_CARRIERS }));
    }
  }, [carrierPartnerId, couriers]);

  // Only the carrier, store and dates are sent; grouping and the chart
  // toggles regroup the report already loaded.
  useEffect(() => {
    // A date input reads "" (or a year like 0002) while a day is half typed.
    if (!carrierPartnerId || !isReportDay(dateFrom) || !isReportDay(dateTo)) {
      return;
    }
    getParcelReport({
      carrierPartnerId,
      dateFrom,
      dateTo,
      locationIds: locationId ? [locationId] : undefined,
    });
  }, [carrierPartnerId, locationId, dateFrom, dateTo, getParcelReport]);

  /** Callbacks */
  const updateFilters = (changes: Partial<IParcelReportFilters>) =>
    setFilters((prev) => ({ ...prev, ...changes }));

  const carrierOptions = useMemo(
    () =>
      couriers?.length
        ? [
            { value: ALL_CARRIERS, label: "All carriers" },
            ...couriers.map((c) => ({
              value: String(c?._id ?? ""),
              label: c?.name ?? "",
            })),
          ]
        : [],
    [couriers]
  );

  const storeOptions = useMemo(() => {
    const ownStore = dropLocations?.find(
      (l) => l?._id === currentUser?.defaultLocation
    );
    // Stores that take parcels, plus any store in the report, so a store
    // switched off later can still be picked for its history.
    const inReport = new Set(
      (parcelReport?.stores ?? []).map((s) => String(s.locationId))
    );
    return [
      {
        value: "",
        label: isStoreAdmin ? storeLabel(ownStore) || "Your store" : "All stores",
      },
      ...(dropLocations ?? [])
        .filter(
          (l) =>
            l?.parcelHandoffEnabled ||
            inReport.has(String(l?._id)) ||
            String(l?._id) === locationId
        )
        .map((l) => ({
          value: String(l?._id ?? ""),
          label: storeLabel(l) || String(l?._id ?? ""),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [
    dropLocations,
    isStoreAdmin,
    currentUser?.defaultLocation,
    parcelReport,
    locationId,
  ]);

  const buckets = useMemo(
    () => buildBuckets(parcelReport?.days ?? [], groupBy),
    [parcelReport, groupBy]
  );
  const stores = useMemo(() => reportStores(parcelReport), [parcelReport]);
  const series = useMemo(
    () => buildSeries(parcelReport?.rows ?? [], buckets, stores),
    [parcelReport, buckets, stores]
  );

  // Like the name, follows the report on screen while the next one loads.
  const isAllCarriers =
    (parcelReport?.carrier?._id ?? carrierPartnerId) === ALL_CARRIERS;
  const carrierName = isAllCarriers
    ? "All carriers"
    : parcelReport?.carrier?.name ||
      couriers?.find((c) => String(c?._id) === carrierPartnerId)?.name ||
      "Carrier";
  const firstDropTitle = isAllCarriers
    ? "First drop was a parcel"
    : `First drop was ${carrierName}`;

  const fileName = (chart: string) =>
    `parcel_report_${fileSlug(carrierName)}_${chart}_${
      GROUPING_SLUGS[groupBy]
    }_${parcelReport?.dateFrom ?? dateFrom}_${parcelReport?.dateTo ?? dateTo}`;

  return (
    <div>
      <div className="flex w-full justify-between">
        <h1 className="font-satoshi text-2xl font-bold text-dark mb-11">
          Parcel report
        </h1>
      </div>

      <ParcelReportFilters
        filters={filters}
        onChange={updateFilters}
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
        carrierOptions={carrierOptions}
        storeOptions={storeOptions}
        isStoreFixed={isStoreAdmin}
      />

      {couriersLoaded && !couriers?.length ? (
        <div className="w-full px-7 py-7 bg-white rounded-xl border-gray-border drop-shadow-none">
          <p className="text-center font-satoshi text-base text-dark">
            No carrier partner to report on. Reload the page, or add a carrier
            under Partners, Carrier partners.
          </p>
        </div>
      ) : error ? (
        <div className="w-full px-7 py-7 bg-white rounded-xl border-gray-border drop-shadow-none">
          <p className="text-center font-satoshi text-base text-red-600">
            {error}
          </p>
        </div>
      ) : (
        // While the next report loads the last one stays, dimmed, rather
        // than every card flashing back to a spinner.
        <div
          aria-busy={isLoading}
          className={`transition-opacity ${
            isLoading && parcelReport ? "opacity-50" : ""
          }`}
        >
          <ParcelReportKpis report={parcelReport} firstDropTitle={firstDropTitle} />

          <ParcelReportChart
            title={isAllCarriers ? "Parcels, all carriers" : `${carrierName} parcels`}
            info="Parcels dropped off and picked up at the stores, as registered in the Droppie app."
            buckets={buckets.buckets}
            bucketHeading={BUCKET_HEADINGS[groupBy]}
            yAxisTitle="Parcels"
            series={
              parcelsView === "type" ? series.byType : series.parcelsByStore
            }
            isLoading={!parcelReport}
            emptyText={
              isAllCarriers
                ? "No parcels in this period."
                : `No ${carrierName} parcels in this period.`
            }
            fileName={fileName(
              parcelsView === "type" ? "parcels_by_type" : "parcels_by_store"
            )}
            controls={
              <SegmentedToggle
                value={parcelsView}
                options={PARCELS_VIEW_OPTIONS}
                onChange={setParcelsView}
              />
            }
          />

          <ParcelReportChart
            title="Stamps given"
            info="One stamp is one parcel counted on a stamp card: the carrier's own card or a shared Droppie card."
            buckets={buckets.buckets}
            bucketHeading={BUCKET_HEADINGS[groupBy]}
            yAxisTitle="Stamps"
            series={series.stampsByStore}
            isLoading={!parcelReport}
            emptyText="No stamps given in this period."
            fileName={fileName("stamps")}
          />

          {parcelReport?.storeIndex ? (
            <ParcelReportIndexChart
              storeIndex={parcelReport.storeIndex}
              days={parcelReport.days}
              buckets={buckets}
              bucketHeading={BUCKET_HEADINGS[groupBy]}
              carrierName={carrierName}
              locationId={locationId}
              cumulative={indexView === "cumulative"}
              fileName={fileName(
                indexView === "cumulative"
                  ? "store_index_cumulative"
                  : "store_index"
              )}
              controls={
                <SegmentedToggle
                  value={indexView}
                  options={[
                    { value: "period", label: PER_PERIOD_LABELS[groupBy] },
                    { value: "cumulative", label: "Cumulative" },
                  ]}
                  onChange={setIndexView}
                />
              }
            />
          ) : null}

          <ParcelReportChart
            title={firstDropTitle}
            info={
              isAllCarriers
                ? "People whose first-ever Droppie drop was a parcel, with any carrier, counted on the day and at the store of that parcel."
                : `People whose first-ever Droppie drop was a ${carrierName} parcel, counted on the day and at the store of that parcel.`
            }
            buckets={buckets.buckets}
            bucketHeading={BUCKET_HEADINGS[groupBy]}
            yAxisTitle="People"
            series={series.firstDropsByStore}
            isLoading={!parcelReport}
            emptyText={
              isAllCarriers
                ? "Nobody's first drop was a parcel in this period."
                : `Nobody's first drop was a ${carrierName} parcel in this period.`
            }
            fileName={fileName("first_drops")}
          />
        </div>
      )}
    </div>
  );
}

export default withProtectedRoute(ParcelReportScreen);
